import { api, type PushSupportSnapshot } from "../api/client";

const PUSH_WAIT_MS = 30000;
const SW_SCOPE = "/";
const SW_SCRIPT = "/service-worker.js";

export function getPushSupport(): PushSupportSnapshot {
  const isIos = /iphone|ipad|ipod/i.test(navigator.userAgent);
  const standalone = window.matchMedia?.("(display-mode: standalone)").matches
    || (window.navigator as Navigator & { standalone?: boolean }).standalone === true;
  const registrationSupportsPush = "ServiceWorkerRegistration" in window
    && "pushManager" in ServiceWorkerRegistration.prototype;
  const supported = "serviceWorker" in navigator
    && ("PushManager" in window || registrationSupportsPush)
    && "Notification" in window;
  return {
    supported,
    isIos,
    standalone,
    needsHomeScreenInstall: isIos && !standalone,
    permission: "Notification" in window ? Notification.permission : "unsupported"
  };
}

function timeout<T>(promise: Promise<T>, message: string, ms = PUSH_WAIT_MS): Promise<T> {
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        window.clearTimeout(timer);
        reject(error);
      }
    );
  });
}

function hasPushManager(registration: ServiceWorkerRegistration) {
  return "pushManager" in (registration as ServiceWorkerRegistration & { pushManager?: PushManager });
}

export async function migrateLegacyAppPushRegistration(rootRegistration: ServiceWorkerRegistration) {
  if (!("serviceWorker" in navigator)) return false;
  const legacy = await navigator.serviceWorker.getRegistration("/app/");
  if (!legacy || legacy.scope === rootRegistration.scope) return false;
  const legacySubscription = "pushManager" in legacy ? await legacy.pushManager.getSubscription() : null;
  if (!legacySubscription) {
    await legacy.unregister();
    return true;
  }
  if (!("pushManager" in rootRegistration) || !("Notification" in window) || Notification.permission !== "granted") return false;
  try {
    const { key } = await api.getVapidPublicKey();
    const rootSubscription = await rootRegistration.pushManager.getSubscription()
      || await rootRegistration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(key) as BufferSource
      });
    await api.subscribePush(rootSubscription.toJSON());
    await api.unsubscribePush(legacySubscription.endpoint);
    await legacySubscription.unsubscribe();
    await legacy.unregister();
    return true;
  } catch {
    // Keep the working legacy subscription and registration until migration can
    // complete in an authenticated session (for example from Settings).
    return false;
  }
}

// Register (or reuse) the root service worker and wait until it is active with a
// pushManager. iOS Home Screen web apps are finicky here: a freshly registered
// worker can sit in installing/waiting without firing statechange, and
// navigator.serviceWorker.ready only resolves once a worker controls the page.
// We cover all three signals: statechange, controllerchange, and the .ready
// promise, plus a polling fallback that re-fetches the registration.
async function ensureServiceWorkerRegistration() {
  if (!("serviceWorker" in navigator)) throw new Error("Service workers are not available in this browser.");

  const existing = await navigator.serviceWorker.getRegistration(SW_SCOPE);
  const registration = existing
    || await navigator.serviceWorker.register(SW_SCRIPT, { scope: SW_SCOPE, updateViaCache: "none" });

  // If a worker is waiting to activate (common right after a deploy or first
  // install on iOS), nudge it. The SW calls skipWaiting() on install, so an
  // update check + claim resolves most stuck states without a manual reload.
  if (registration.waiting || registration.installing) {
    try {
      await registration.update();
    } catch {
      /* update can reject if the network is unavailable; the pollers below still run */
    }
  }

  if (registration.active && hasPushManager(registration)) {
    await migrateLegacyAppPushRegistration(registration);
    return registration;
  }

  const active = await timeout(
    waitForActiveRegistration(registration),
    "The service worker is not active yet. Close and reopen the Home Screen app, then try again."
  );
  await migrateLegacyAppPushRegistration(active);
  return active;
}

function waitForActiveRegistration(registration: ServiceWorkerRegistration): Promise<ServiceWorkerRegistration> {
  return new Promise((resolve) => {
    let pollTimer = 0;
    const settle = (value: ServiceWorkerRegistration | null | undefined) => {
      if (value?.active && hasPushManager(value)) {
        window.clearInterval(pollTimer);
        cleanup();
        resolve(value);
      }
    };

    const cleanup = () => {
      window.clearInterval(pollTimer);
      registration.installing?.removeEventListener("statechange", onState);
      registration.waiting?.removeEventListener("statechange", onState);
      navigator.serviceWorker.removeEventListener("controllerchange", onController);
    };

    const poll = async () => {
      const current = await navigator.serviceWorker.getRegistration(SW_SCOPE);
      settle(current);
    };

    const onState = () => { void poll(); };
    const onController = () => {
      void navigator.serviceWorker.getRegistration(SW_SCOPE).then(settle);
    };

    // 1) state transitions of the installing/waiting worker
    registration.installing?.addEventListener("statechange", onState);
    registration.waiting?.addEventListener("statechange", onState);
    // 2) a new worker taking control of this client (skipWaiting + claim)
    navigator.serviceWorker.addEventListener("controllerchange", onController);
    // 3) the canonical ready signal (resolves when a SW controls the page)
    navigator.serviceWorker.ready.then(settle, () => {});
    // 4) polling fallback — iOS occasionally drops the events above
    pollTimer = window.setInterval(() => void poll(), 500);
    void poll();
  });
}

function urlBase64ToUint8Array(base64: string) {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(normalized);
  return Uint8Array.from([...raw].map((ch) => ch.charCodeAt(0)));
}

export async function getCurrentSubscription() {
  if (!("serviceWorker" in navigator)) return null;
  const registration = await timeout(
    ensureServiceWorkerRegistration(),
    "The service worker did not become ready."
  );
  if (!("pushManager" in registration)) return null;
  return registration.pushManager.getSubscription();
}

export async function enablePush(onProgress?: (message: string) => void) {
  const support = getPushSupport();
  if (support.needsHomeScreenInstall && !support.supported) {
    throw new Error("Install notey-g from Safari's Share menu first, then open the Home Screen app to enable push.");
  }
  if (!support.supported && !support.isIos) throw new Error("Notifications are not supported in this browser.");
  if (!("Notification" in window)) throw new Error("Notifications are not supported in this browser.");
  onProgress?.("Requesting notification permission...");
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error(permission === "denied"
      ? "Notifications are blocked for this site."
      : "Notification permission was not granted.");
  }
  onProgress?.("Activating service worker...");
  const { key } = await api.getVapidPublicKey();
  const registration = await ensureServiceWorkerRegistration();
  if (!("pushManager" in registration)) throw new Error("Notifications are not available in this browser.");
  onProgress?.("Creating push subscription...");
  const subscription = await registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(key) as BufferSource
  });
  onProgress?.("Saving this device...");
  await api.subscribePush(subscription.toJSON());
  return subscription;
}

export async function disablePush() {
  const subscription = await getCurrentSubscription();
  if (!subscription) return;
  await api.unsubscribePush(subscription.endpoint);
  await subscription.unsubscribe();
}
